import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

const AICommandCenter = () => {
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(false);
    
    const [stats, setStats] = useState({
        activeAgents: 12,
        completedTasks: 1847,
        efficiency: '96.8%',
        totalAgents: 15,
        queuedTasks: 23,
        avgResponseTime: '1.2s',
        tokensUsed: '2.4M',
        costSaved: '$12,450'
    });

    const [agents, setAgents] = useState([
        { 
            id: 1, 
            name: 'GTM Strategist Pro', 
            role: 'Go-to-Market Strategy', 
            status: 'active', 
            icon: '🎯',
            tasks: 34,
            successRate: 98.2,
            lastActive: '2 min ago',
            specialty: 'Market analysis, ICP mapping, competitive positioning',
            tier: 'premium'
        },
        { 
            id: 2, 
            name: 'Content Maven', 
            role: 'Content Generation', 
            status: 'active', 
            icon: '✍️',
            tasks: 127,
            successRate: 94.5,
            lastActive: '5 min ago',
            specialty: 'Blog posts, social media, email campaigns',
            tier: 'standard'
        },
        { 
            id: 3, 
            name: 'SEO Optimizer Max', 
            role: 'Search Optimization', 
            status: 'active', 
            icon: '📈',
            tasks: 89,
            successRate: 96.1,
            lastActive: '1 min ago',
            specialty: 'Keyword research, on-page SEO, technical audits',
            tier: 'premium'
        },
        { 
            id: 4, 
            name: 'Data Analytics Engine', 
            role: 'Analytics & Insights', 
            status: 'active', 
            icon: '📊',
            tasks: 156,
            successRate: 99.1,
            lastActive: 'Just now',
            specialty: 'Performance tracking, predictive analytics, reporting',
            tier: 'enterprise'
        },
        { 
            id: 5, 
            name: 'Campaign Orchestrator', 
            role: 'Campaign Management', 
            status: 'busy', 
            icon: '⚡',
            tasks: 78,
            successRate: 95.7,
            lastActive: 'Just now',
            specialty: 'Multi-channel campaigns, automation, A/B testing',
            tier: 'premium'
        },
        { 
            id: 6, 
            name: 'Audience Intelligence', 
            role: 'Audience Research', 
            status: 'active', 
            icon: '👥',
            tasks: 92,
            successRate: 97.3,
            lastActive: '3 min ago',
            specialty: 'Psychographic profiling, sentiment analysis, persona building',
            tier: 'enterprise'
        },
        { 
            id: 7, 
            name: 'Social Media Commander', 
            role: 'Social Media', 
            status: 'active', 
            icon: '📱',
            tasks: 203,
            successRate: 93.8,
            lastActive: '4 min ago',
            specialty: 'Post scheduling, engagement tracking, influencer outreach',
            tier: 'standard'
        },
        { 
            id: 8, 
            name: 'Email Marketing Wizard', 
            role: 'Email Campaigns', 
            status: 'idle', 
            icon: '📧',
            tasks: 145,
            successRate: 96.9,
            lastActive: '15 min ago',
            specialty: 'Drip campaigns, segmentation, deliverability optimization',
            tier: 'premium'
        },
        { 
            id: 9, 
            name: 'Competitive Intel', 
            role: 'Competitive Analysis', 
            status: 'active', 
            icon: '🔍',
            tasks: 67,
            successRate: 98.7,
            lastActive: '1 min ago',
            specialty: 'Competitor tracking, market positioning, SWOT analysis',
            tier: 'premium'
        },
        { 
            id: 10, 
            name: 'Conversion Optimizer', 
            role: 'CRO & Testing', 
            status: 'active', 
            icon: '🎲',
            tasks: 54,
            successRate: 94.2,
            lastActive: '8 min ago',
            specialty: 'Landing page optimization, funnel analysis, multivariate testing',
            tier: 'enterprise'
        },
        { 
            id: 11, 
            name: 'Sales Signal Detector', 
            role: 'Sales Intelligence', 
            status: 'active', 
            icon: '💎',
            tasks: 112,
            successRate: 97.8,
            lastActive: 'Just now',
            specialty: 'Intent detection, lead scoring, buying signals',
            tier: 'enterprise'
        },
        { 
            id: 12, 
            name: 'Voice AI Assistant', 
            role: 'Voice Communications', 
            status: 'active', 
            icon: '🎙️',
            tasks: 43,
            successRate: 91.5,
            lastActive: '2 min ago',
            specialty: 'Call handling, transcription, voice analytics',
            tier: 'premium'
        }
    ]);

    const [tasks, setTasks] = useState([
        { 
            id: 1, 
            title: 'Q4 2025 Market Positioning Analysis', 
            agent: 'GTM Strategist Pro', 
            status: 'completed', 
            time: '45 min ago',
            duration: '12 min',
            priority: 'high',
            type: 'research',
            company: 'TechCorp Solutions',
            output: 'Full market report with 5 strategic recommendations'
        },
        { 
            id: 2, 
            title: 'Generate 10 LinkedIn Posts for Product Launch', 
            agent: 'Content Maven', 
            status: 'in_progress', 
            time: '5 min ago',
            duration: '18 min',
            priority: 'medium',
            type: 'content',
            company: 'SaaS Startup Inc',
            progress: 70
        },
        { 
            id: 3, 
            title: 'SEO Audit for E-commerce Site (50 pages)', 
            agent: 'SEO Optimizer Max', 
            status: 'in_progress', 
            time: '22 min ago',
            duration: '35 min',
            priority: 'high',
            type: 'seo',
            company: 'Fashion Retailer',
            progress: 85
        },
        { 
            id: 4, 
            title: 'Weekly Performance Dashboard Generation', 
            agent: 'Data Analytics Engine', 
            status: 'completed', 
            time: '1 hour ago',
            duration: '8 min',
            priority: 'medium',
            type: 'analytics',
            company: 'Global Logistics',
            output: '24-page insights report with 15 visualizations'
        },
        { 
            id: 5, 
            title: 'Multi-Channel Campaign Setup: Summer Sale 2025', 
            agent: 'Campaign Orchestrator', 
            status: 'in_progress', 
            time: '10 min ago',
            duration: '45 min',
            priority: 'critical',
            type: 'campaign',
            company: 'Retail Chain',
            progress: 40
        },
        { 
            id: 6, 
            title: 'Customer Sentiment Analysis (5,000 reviews)', 
            agent: 'Audience Intelligence', 
            status: 'completed', 
            time: '2 hours ago',
            duration: '25 min',
            priority: 'medium',
            type: 'research',
            company: 'Consumer Electronics',
            output: 'Sentiment report + 8 key themes identified'
        },
        { 
            id: 7, 
            title: 'Competitive Pricing Strategy Analysis', 
            agent: 'Competitive Intel', 
            status: 'completed', 
            time: '3 hours ago',
            duration: '19 min',
            priority: 'high',
            type: 'research',
            company: 'B2B Software',
            output: 'Pricing matrix + positioning recommendations'
        },
        { 
            id: 8, 
            title: 'Email Drip Campaign: Onboarding Sequence', 
            agent: 'Email Marketing Wizard', 
            status: 'queued', 
            time: 'Scheduled for 2:00 PM',
            priority: 'low',
            type: 'email',
            company: 'FinTech Platform'
        },
        { 
            id: 9, 
            title: 'Sales Intent Signal Detection (250 leads)', 
            agent: 'Sales Signal Detector', 
            status: 'in_progress', 
            time: '8 min ago',
            duration: '15 min',
            priority: 'critical',
            type: 'sales',
            company: 'Enterprise SaaS',
            progress: 60
        },
        { 
            id: 10, 
            title: 'Landing Page CRO Analysis + Recommendations', 
            agent: 'Conversion Optimizer', 
            status: 'completed', 
            time: '4 hours ago',
            duration: '22 min',
            priority: 'high',
            type: 'optimization',
            company: 'Marketing Agency',
            output: '12 optimization opportunities identified'
        }
    ]);

    const [workflows, setWorkflows] = useState([
        {
            id: 1,
            name: 'Full Product Launch Pipeline',
            description: 'End-to-end product launch from research to execution',
            agents: ['GTM Strategist Pro', 'Content Maven', 'SEO Optimizer Max', 'Campaign Orchestrator', 'Social Media Commander'],
            steps: 8,
            avgDuration: '4-6 hours',
            runs: 23,
            successRate: 94.2,
            status: 'active',
            icon: '🚀'
        },
        {
            id: 2,
            name: 'Content Marketing Automation',
            description: 'Weekly blog + social + email content generation',
            agents: ['Content Maven', 'SEO Optimizer Max', 'Social Media Commander', 'Email Marketing Wizard'],
            steps: 5,
            avgDuration: '2-3 hours',
            runs: 67,
            successRate: 96.8,
            status: 'active',
            icon: '📝'
        },
        {
            id: 3,
            name: 'Competitive Intelligence Monitor',
            description: 'Daily competitive tracking and alerts',
            agents: ['Competitive Intel', 'Data Analytics Engine', 'Sales Signal Detector'],
            steps: 4,
            avgDuration: '30-45 min',
            runs: 156,
            successRate: 98.1,
            status: 'active',
            icon: '🎯'
        },
        {
            id: 4,
            name: 'Lead Nurture Engine',
            description: 'Automated lead scoring, segmentation, and nurturing',
            agents: ['Sales Signal Detector', 'Email Marketing Wizard', 'Audience Intelligence'],
            steps: 6,
            avgDuration: '1-2 hours',
            runs: 89,
            successRate: 95.3,
            status: 'active',
            icon: '💼'
        }
    ]);

    const [performance, setPerformance] = useState({
        daily: [
            { date: '11/20', tasks: 45, success: 43, failed: 2 },
            { date: '11/21', tasks: 52, success: 50, failed: 2 },
            { date: '11/22', tasks: 61, success: 59, failed: 2 },
            { date: '11/23', tasks: 48, success: 47, failed: 1 },
            { date: '11/24', tasks: 55, success: 53, failed: 2 },
            { date: '11/25', tasks: 67, success: 65, failed: 2 },
            { date: '11/26', tasks: 73, success: 71, failed: 2 }
        ],
        agentUtilization: [
            { agent: 'GTM Strategist Pro', utilization: 85 },
            { agent: 'Content Maven', utilization: 92 },
            { agent: 'SEO Optimizer Max', utilization: 78 },
            { agent: 'Data Analytics Engine', utilization: 95 },
            { agent: 'Campaign Orchestrator', utilization: 88 }
        ]
    });

    useEffect(() => {
        // Simulate real-time updates
        const interval = setInterval(() => {
            // Update task progress
            setTasks(prevTasks => prevTasks.map(task => {
                if (task.status === 'in_progress' && task.progress) {
                    const newProgress = Math.min(task.progress + Math.random() * 10, 100);
                    if (newProgress >= 100) {
                        return { ...task, status: 'completed', progress: 100 };
                    }
                    return { ...task, progress: newProgress };
                }
                return task;
            }));
        }, 3000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        // Simulate real-time updates
        const interval = setInterval(() => {
            // Update task progress
            setTasks(prevTasks => prevTasks.map(task => {
                if (task.status === 'in_progress' && task.progress) {
                    const newProgress = Math.min(task.progress + Math.random() * 10, 100);
                    if (newProgress >= 100) {
                        return { ...task, status: 'completed', progress: 100 };
                    }
                    return { ...task, progress: newProgress };
                }
                return task;
            }));
        }, 3000);

        return () => clearInterval(interval);
    }, []);

    const getStatusColor = (status) => {
        const colors = {
            active: '#10b981',
            busy: '#f59e0b',
            idle: '#6b7280',
            error: '#ef4444',
            completed: '#10b981',
            in_progress: '#3b82f6',
            queued: '#8b5cf6',
            failed: '#ef4444'
        };
        return colors[status] || '#6b7280';
    };

    const getPriorityColor = (priority) => {
        const colors = {
            critical: '#dc2626',
            high: '#f59e0b',
            medium: '#3b82f6',
            low: '#6b7280'
        };
        return colors[priority] || '#6b7280';
    };

    const getTypeIcon = (type) => {
        const icons = {
            research: '🔍',
            content: '✍️',
            seo: '📈',
            analytics: '📊',
            campaign: '⚡',
            email: '📧',
            sales: '💎',
            optimization: '🎲',
            social: '📱'
        };
        return icons[type] || '📋';
    };

    const renderOverviewTab = () => (
        <>
            {/* Key Metrics Grid */}
            <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                marginBottom: '2rem'
            }}>
                <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                }}>
                    <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Active Agents</div>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        {stats.activeAgents}/{stats.totalAgents}
                    </div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>80% utilization</div>
                </div>

                <div style={{
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    boxShadow: '0 4px 12px rgba(240, 147, 251, 0.3)'
                }}>
                    <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Tasks Completed</div>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        {stats.completedTasks.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>+127 this week</div>
                </div>

                <div style={{
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    boxShadow: '0 4px 12px rgba(79, 172, 254, 0.3)'
                }}>
                    <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Success Rate</div>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        {stats.efficiency}
                    </div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>↑ 2.3% from last month</div>
                </div>

                <div style={{
                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '1.5rem',
                    boxShadow: '0 4px 12px rgba(67, 233, 123, 0.3)'
                }}>
                    <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '0.5rem' }}>Cost Saved</div>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        {stats.costSaved}
                    </div>
                    <div style={{ fontSize: '0.75rem', opacity: 0.8 }}>vs manual work</div>
                </div>

                <div style={{
                    background: isDark ? '#1f2937' : 'white',
                    border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                    borderRadius: '12px',
                    padding: '1.5rem'
                }}>
                    <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>Queued Tasks</div>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--gray-900)', marginBottom: '0.25rem' }}>
                        {stats.queuedTasks}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Avg wait: {stats.avgResponseTime}</div>
                </div>

                <div style={{
                    background: isDark ? '#1f2937' : 'white',
                    border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                    borderRadius: '12px',
                    padding: '1.5rem'
                }}>
                    <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginBottom: '0.5rem' }}>Tokens Used</div>
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: 'var(--gray-900)', marginBottom: '0.25rem' }}>
                        {stats.tokensUsed}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>This month</div>
                </div>
            </div>

            {/* Performance Chart */}
            <div style={{
                background: isDark ? '#1f2937' : 'white',
                border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '2rem'
            }}>
                <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '1.5rem' }}>
                    📈 Task Performance (Last 7 Days)
                </h3>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', height: '200px' }}>
                    {performance.daily.map((day, index) => (
                        <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', width: '100%', gap: '4px' }}>
                                <div
                                    style={{
                                        width: '100%',
                                        height: `${(day.success / Math.max(...performance.daily.map(d => d.tasks))) * 100}%`,
                                        background: 'linear-gradient(180deg, #10b981 0%, #059669 100%)',
                                        borderRadius: '6px 6px 0 0',
                                        position: 'relative'
                                    }}
                                    title={`Success: ${day.success}`}
                                />
                                {day.failed > 0 && (
                                    <div
                                        style={{
                                            width: '100%',
                                            height: `${(day.failed / Math.max(...performance.daily.map(d => d.tasks))) * 100 * 2}%`,
                                            background: 'linear-gradient(180deg, #ef4444 0%, #dc2626 100%)',
                                            borderRadius: '6px 6px 0 0'
                                        }}
                                        title={`Failed: ${day.failed}`}
                                    />
                                )}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)', marginTop: '0.5rem' }}>
                                {day.date}
                            </div>
                        </div>
                    ))}
                </div>
                <div style={{ display: 'flex', gap: '1.5rem', marginTop: '1rem', fontSize: '0.875rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ width: '12px', height: '12px', background: '#10b981', borderRadius: '3px' }} />
                        <span className="breadcrumb-current">Successful</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ width: '12px', height: '12px', background: '#ef4444', borderRadius: '3px' }} />
                        <span className="breadcrumb-current">Failed</span>
                    </div>
                </div>
            </div>

            {/* Recent Activity */}
            <div style={{
                background: isDark ? '#1f2937' : 'white',
                border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                borderRadius: '12px',
                padding: '1.5rem'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', margin: 0 }}>
                        🕐 Recent Activity
                    </h3>
                    <button className="btn btn-secondary" style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}>
                        View All
                    </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {tasks.slice(0, 5).map(task => (
                        <div
                            key={task.id}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1rem',
                                padding: '1rem',
                                background: isDark ? '#374151' : 'var(--gray-50)',
                                borderRadius: '8px',
                                borderLeft: `4px solid ${getStatusColor(task.status)}`
                            }}
                        >
                            <div style={{ fontSize: '1.5rem' }}>{getTypeIcon(task.type)}</div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '600', color: 'var(--gray-900)', marginBottom: '0.25rem' }}>
                                    {task.title}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                                    {task.agent} • {task.company}
                                </div>
                            </div>
                            <div style={{
                                padding: '0.25rem 0.75rem',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                backgroundColor: `${getStatusColor(task.status)}20`,
                                color: getStatusColor(task.status),
                                textTransform: 'capitalize'
                            }}>
                                {task.status.replace('_', ' ')}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', minWidth: '80px', textAlign: 'right' }}>
                                {task.time}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );

    const renderWizardsTab = () => (
        <>
            <div className="card" style={{ marginBottom: '2rem' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    paddingBottom: '1.5rem',
                    borderBottom: '3px solid #EF4444'
                }}>
                    <div>
                        <h2 style={{
                            color: 'var(--gray-900)',
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            margin: '0 0 0.25rem 0'
                        }}>
                            🧙 AI Wizards
                        </h2>
                        <p style={{ color: 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
                            Guided AI-powered workflows for common marketing tasks
                        </p>
                    </div>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '1.5rem'
                }}>
                    {[
                        { icon: '🎯', title: 'Campaign Wizard', desc: 'End-to-end campaign creation from strategy to execution', path: '/ai/wizards/campaign' },
                        { icon: '✍️', title: 'Content Generator', desc: 'Generate blog posts, social content, and marketing copy', path: '/ai/wizards/content' },
                        { icon: '🔍', title: 'Market Research', desc: 'Deep dive into competitors, trends, and audience insights', path: '/ai/wizards/research' },
                        { icon: '📈', title: 'SEO Optimizer', desc: 'Audit and optimize your site for search engines', path: '/ai/wizards/seo' },
                        { icon: '📧', title: 'Email Sequences', desc: 'Create automated email nurture sequences', path: '/ai/wizards/email' },
                        { icon: '📊', title: 'Analytics Report', desc: 'Generate comprehensive performance reports', path: '/ai/wizards/analytics' }
                    ].map((wizard, idx) => (
                        <div
                            key={idx}
                            onClick={() => navigate(wizard.path)}
                            style={{
                                background: isDark ? '#1f2937' : 'white',
                                border: '2px solid var(--gray-200)',
                                borderRadius: '8px',
                                padding: '1.5rem',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#667eea';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--gray-200)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                        >
                            <div style={{ fontSize: '2rem', marginBottom: '1rem', color: '#667eea' }}>{wizard.icon}</div>
                            <h3 style={{ fontSize: '1.125rem', color: 'var(--gray-900)', margin: '0 0 0.5rem 0', fontWeight: '600' }}>
                                {wizard.title}
                            </h3>
                            <p style={{ color: 'var(--gray-600)', fontSize: '0.875rem', margin: 0 }}>{wizard.desc}</p>
                        </div>
                    ))}
                </div>
            </div>
        </>
    );

    const renderAgentsTab = () => (
        <>
            {/* Breadcrumb Navigation */}
            <nav className="breadcrumb">
                <button
                    onClick={() => navigate('/dashboard')}
                    className="breadcrumb-link"
                >
                    Dashboard
                </button>
                <span className="breadcrumb-separator">/</span>
                <span className="breadcrumb-current">
                    AI Command Center
                </span>
            </nav>

            {/* Main Card */}
            <div className="card" style={{ marginBottom: '2rem' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    paddingBottom: '1.5rem',
                    borderBottom: '3px solid #EF4444'
                }}>
                    <div>
                        <h2 style={{
                            color: 'var(--gray-900)',
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            margin: '0 0 0.25rem 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem'
                        }}>
                            🤖 AI Command Center
                            <span style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                padding: '0.25rem 0.75rem',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                letterSpacing: '0.5px'
                            }}>
                                ENTERPRISE
                            </span>
                        </h2>
                        <p style={{
                            color: 'var(--gray-600)',
                            margin: 0,
                            fontSize: '0.875rem'
                        }}>
                            Manage and monitor your AI agents and automated workflows
                        </p>
                    </div>
                    <button
                        onClick={() => navigate('/ai/new-task')}
                        className="btn btn-primary"
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        + New Mission
                    </button>
                </div>

                {/* Key Metrics */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '1.5rem',
                    marginBottom: '2rem'
                }}>
                    <div style={{
                        background: isDark ? '#1f2937' : 'white',
                        border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '1rem'
                        }}>
                            <h3 style={{
                                fontSize: '1rem',
                                color: 'var(--gray-700)',
                                margin: 0,
                                fontWeight: '600'
                            }}>
                                Active Agents
                            </h3>
                            <span style={{ fontSize: '1.5rem' }}>👥</span>
                        </div>
                        <div style={{
                            fontSize: '2rem',
                            fontWeight: 'bold',
                            color: 'var(--gray-900)',
                            marginBottom: '0.5rem'
                        }}>
                            {stats.activeAgents}
                        </div>
                        <div style={{
                            color: 'var(--gray-600)',
                            fontSize: '0.875rem'
                        }}>
                            Currently online and processing
                        </div>
                    </div>

                    <div style={{
                        background: isDark ? '#1f2937' : 'white',
                        border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '1rem'
                        }}>
                            <h3 style={{
                                fontSize: '1rem',
                                color: 'var(--gray-700)',
                                margin: 0,
                                fontWeight: '600'
                            }}>
                                Tasks Completed
                            </h3>
                            <span style={{ fontSize: '1.5rem' }}>✅</span>
                        </div>
                        <div style={{
                            fontSize: '2rem',
                            fontWeight: 'bold',
                            color: 'var(--gray-900)',
                            marginBottom: '0.5rem'
                        }}>
                            {stats.completedTasks}
                        </div>
                        <div style={{
                            color: 'var(--gray-600)',
                            fontSize: '0.875rem'
                        }}>
                            In the last 30 days
                        </div>
                    </div>

                    <div style={{
                        background: isDark ? '#1f2937' : 'white',
                        border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '1rem'
                        }}>
                            <h3 style={{
                                fontSize: '1rem',
                                color: 'var(--gray-700)',
                                margin: 0,
                                fontWeight: '600'
                            }}>
                                System Efficiency
                            </h3>
                            <span style={{ fontSize: '1.5rem' }}>🚀</span>
                        </div>
                        <div style={{
                            fontSize: '2rem',
                            fontWeight: 'bold',
                            color: 'var(--gray-900)',
                            marginBottom: '0.5rem'
                        }}>
                            {stats.efficiency}
                        </div>
                        <div style={{
                            color: 'var(--gray-600)',
                            fontSize: '0.875rem'
                        }}>
                            Average success rate
                        </div>
                    </div>
                </div>

                {/* Main Content Area */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr',
                    gap: '1.5rem',
                    marginBottom: '2rem'
                }}>
                    {/* Active Agents List */}
                    <div style={{
                        background: isDark ? '#1f2937' : 'white',
                        border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '1rem'
                        }}>
                            <h3 style={{
                                fontSize: '1.125rem',
                                color: 'var(--gray-900)',
                                margin: 0,
                                fontWeight: '600'
                            }}>
                                Agent Status
                            </h3>
                        </div>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem'
                        }}>
                            {agents.map(agent => (
                                <div key={agent.id} style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '1rem',
                                    padding: '0.75rem',
                                    background: isDark ? '#374151' : 'var(--gray-50)',
                                    borderRadius: '8px',
                                    borderLeft: `4px solid ${agent.status === 'active' ? '#10b981' : '#e5e7eb'}`
                                }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '50%',
                                        background: isDark ? '#1f2937' : 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '1.25rem',
                                        border: '2px solid var(--gray-200)'
                                    }}>
                                        {agent.icon}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{
                                            fontWeight: '600',
                                            color: 'var(--gray-900)',
                                            marginBottom: '0.25rem'
                                        }}>
                                            {agent.name}
                                        </div>
                                        <div style={{
                                            fontSize: '0.75rem',
                                            color: 'var(--gray-600)'
                                        }}>
                                            {agent.role}
                                        </div>
                                    </div>
                                    <div style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: agent.status === 'active' ? '#10b981' : '#cbd5e0'
                                    }} />
                                    <span style={{
                                        fontSize: '0.75rem',
                                        color: 'var(--gray-600)',
                                        textTransform: 'capitalize'
                                    }}>
                                        {agent.status}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Recent Activity */}
                    <div style={{
                        background: isDark ? '#1f2937' : 'white',
                        border: isDark ? '1px solid #374151' : '1px solid var(--gray-200)',
                        borderRadius: '8px',
                        padding: '1.5rem',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '1rem'
                        }}>
                            <h3 style={{
                                fontSize: '1.125rem',
                                color: 'var(--gray-900)',
                                margin: 0,
                                fontWeight: '600'
                            }}>
                                Recent Activity
                            </h3>
                        </div>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem'
                        }}>
                            {tasks.slice(0, 5).map(task => (
                                <div key={task.id} style={{
                                    padding: '0.75rem 0',
                                    borderBottom: '1px solid var(--gray-200)'
                                }}>
                                    <div style={{
                                        fontWeight: '500',
                                        color: 'var(--gray-900)',
                                        marginBottom: '0.25rem'
                                    }}>
                                        {task.title}
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        fontSize: '0.75rem',
                                        color: 'var(--gray-600)'
                                    }}>
                                        <span>{task.agent}</span>
                                        <span>{task.time}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* AI Wizards Section */}
                <div style={{ marginTop: '2rem' }}>
                    <h2 style={{
                        fontSize: '1.25rem',
                        color: 'var(--gray-900)',
                        marginBottom: '1rem',
                        fontWeight: '600'
                    }}>
                        🚀 Launch AI Workflow
                    </h2>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                        gap: '1.5rem'
                    }}>
                        <div
                            onClick={() => navigate('/ai/wizards/campaign')}
                            style={{
                                background: isDark ? '#1f2937' : 'white',
                                border: '2px solid var(--gray-200)',
                                borderRadius: '8px',
                                padding: '1.5rem',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#667eea';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--gray-200)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                            }}
                        >
                            <div style={{
                                fontSize: '2rem',
                                marginBottom: '1rem',
                                color: '#667eea'
                            }}>
                                🎯
                            </div>
                            <h3 style={{
                                fontSize: '1.125rem',
                                color: 'var(--gray-900)',
                                margin: '0 0 0.5rem 0',
                                fontWeight: '600'
                            }}>
                                Campaign Wizard
                            </h3>
                            <p style={{
                                color: 'var(--gray-600)',
                                fontSize: '0.875rem',
                                margin: 0
                            }}>
                                End-to-end campaign creation from strategy to execution.
                            </p>
                        </div>

                        <div
                            onClick={() => navigate('/ai/wizards/content')}
                            style={{
                                background: isDark ? '#1f2937' : 'white',
                                border: '2px solid var(--gray-200)',
                                borderRadius: '8px',
                                padding: '1.5rem',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#667eea';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--gray-200)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                            }}
                        >
                            <div style={{
                                fontSize: '2rem',
                                marginBottom: '1rem',
                                color: '#667eea'
                            }}>
                                ✍️
                            </div>
                            <h3 style={{
                                fontSize: '1.125rem',
                                color: 'var(--gray-900)',
                                margin: '0 0 0.5rem 0',
                                fontWeight: '600'
                            }}>
                                Content Studio
                            </h3>
                            <p style={{
                                color: 'var(--gray-600)',
                                fontSize: '0.875rem',
                                margin: 0
                            }}>
                                Generate blog posts, social media content, and emails.
                            </p>
                        </div>

                        <div
                            onClick={() => navigate('/ai/wizards/research')}
                            style={{
                                background: isDark ? '#1f2937' : 'white',
                                border: '2px solid var(--gray-200)',
                                borderRadius: '8px',
                                padding: '1.5rem',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#667eea';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--gray-200)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                            }}
                        >
                            <div style={{
                                fontSize: '2rem',
                                marginBottom: '1rem',
                                color: '#667eea'
                            }}>
                                🔍
                            </div>
                            <h3 style={{
                                fontSize: '1.125rem',
                                color: 'var(--gray-900)',
                                margin: '0 0 0.5rem 0',
                                fontWeight: '600'
                            }}>
                                Market Research
                            </h3>
                            <p style={{
                                color: 'var(--gray-600)',
                                fontSize: '0.875rem',
                                margin: 0
                            }}>
                                Deep dive into competitors, trends, and audience insights.
                            </p>
                        </div>

                        <div
                            onClick={() => navigate('/ai/wizards/seo')}
                            style={{
                                background: isDark ? '#1f2937' : 'white',
                                border: '2px solid var(--gray-200)',
                                borderRadius: '8px',
                                padding: '1.5rem',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = '#667eea';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = 'var(--gray-200)';
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                            }}
                        >
                            <div style={{
                                fontSize: '2rem',
                                marginBottom: '1rem',
                                color: '#667eea'
                            }}>
                                📈
                            </div>
                            <h3 style={{
                                fontSize: '1.125rem',
                                color: 'var(--gray-900)',
                                margin: '0 0 0.5rem 0',
                                fontWeight: '600'
                            }}>
                                SEO Optimizer
                            </h3>
                            <p style={{
                                color: 'var(--gray-600)',
                                fontSize: '0.875rem',
                                margin: 0
                            }}>
                                Audit and optimize your site for search engines.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );

    // Main component return
    return (
        <div>
            {/* Breadcrumb Navigation */}
            <nav className="breadcrumb">
                <button
                    onClick={() => navigate('/dashboard')}
                    className="breadcrumb-link"
                >
                    Dashboard
                </button>
                <span className="breadcrumb-separator">/</span>
                <span className="breadcrumb-current">
                    AI Command Center
                </span>
            </nav>

            {/* Main Card */}
            <div className="card">
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    paddingBottom: '1.5rem',
                    borderBottom: '3px solid #EF4444'
                }}>
                    <div>
                        <h2 style={{
                            color: 'var(--gray-900)',
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            margin: '0 0 0.25rem 0'
                        }}>
                            🤖 AI Command Center
                        </h2>
                        <p style={{
                            color: 'var(--gray-600)',
                            margin: 0,
                            fontSize: '0.875rem'
                        }}>
                            Manage AI agents, tasks, and workflows
                        </p>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    marginBottom: '1.5rem',
                    borderBottom: '2px solid #e5e7eb',
                    paddingBottom: '0.5rem'
                }}>
                    {['overview', 'agents', 'wizards'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            style={{
                                padding: '0.75rem 1.5rem',
                                border: 'none',
                                background: activeTab === tab ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                                color: activeTab === tab ? 'white' : '#6b7280',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                textTransform: 'capitalize',
                                transition: 'all 0.2s'
                            }}
                        >
                            {tab === 'overview' && '📊 '}{tab === 'agents' && '🤖 '}{tab === 'wizards' && '🧙 '}{tab}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                {activeTab === 'overview' && renderOverviewTab()}
                {activeTab === 'agents' && renderAgentsTab()}
                {activeTab === 'wizards' && renderWizardsTab()}
            </div>
        </div>
    );
};

export default AICommandCenter;